/** Tier order from lowest to highest (matches customer portal `config.loyalty.tiersOrder`). */
export const LUX_LOYALTY_TIER_ORDER: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold', 'platinum', 'infinite'];

export interface MembershipYearContext {
  start: Date;
  end: Date;
  reviewYearStart: Date;
  retainVariant: 'current-year' | 'next-year';
}

export interface TierProgressionRetainModeState {
  isRetainMode: boolean;
  isAtRiskOfDowngrade: boolean;
}

export function getNextLuxLoyaltyTier(tier: App.LuxLoyaltyTier): App.LuxLoyaltyTier | undefined {
  const currentIndex = LUX_LOYALTY_TIER_ORDER.indexOf(tier);
  if (currentIndex === -1 || currentIndex >= LUX_LOYALTY_TIER_ORDER.length - 1) {
    return undefined;
  }
  return LUX_LOYALTY_TIER_ORDER[currentIndex + 1];
}

function getJoinMonthCycleStart(endDate: string): Date {
  const end = new Date(endDate);
  return new Date(Date.UTC(end.getUTCFullYear() - 1, end.getUTCMonth() + 1, 1));
}

/**
 * Mirrors customer portal `getMembershipYearContext` — used to determine whether the
 * member's tier review falls in the current membership year.
 */
export function getMembershipYearContext(
  statusReviewDate: string | undefined,
  membershipYearEndDate: string | undefined,
  createdAt: string | undefined,
): MembershipYearContext | undefined {
  if (!statusReviewDate || !membershipYearEndDate || !createdAt) {
    return undefined;
  }

  const end = new Date(membershipYearEndDate);
  const enrolment = new Date(createdAt);
  const isYear1 =
    end.getUTCFullYear() === enrolment.getUTCFullYear() + 1 && end.getUTCMonth() === enrolment.getUTCMonth();
  const start = isYear1 ? enrolment : getJoinMonthCycleStart(membershipYearEndDate);
  const retainVariant: 'current-year' | 'next-year' =
    new Date(statusReviewDate).getTime() > end.getTime() ? 'next-year' : 'current-year';
  const reviewYearStart = retainVariant === 'current-year' ? start : getJoinMonthCycleStart(statusReviewDate);

  return { start, end, reviewYearStart, retainVariant };
}

/**
 * Default retain vs upgrade mode (matches customer portal `getTierProgressionRetainModeState`
 * when tier thresholds are unavailable — uses account retention fields from the API).
 */
export function getTierProgressionRetainModeState(
  account: App.LuxLoyaltyAccount,
  nextTier?: App.LuxLoyaltyTier,
): TierProgressionRetainModeState {
  const tier = account.tier;
  const retentionCreditsThreshold = account.totalCreditsForTierRetention ?? 0;
  const retentionNightsThreshold = account.numberOfNightsForTierRetention ?? 0;
  const isAtRiskOfDowngrade =
    tier !== 'bronze' &&
    (account.statusCredits < retentionCreditsThreshold || account.numberOfNights < retentionNightsThreshold);
  const isRetainMode = !nextTier || isAtRiskOfDowngrade;

  return { isRetainMode, isAtRiskOfDowngrade };
}

export interface TierProgressionThresholds {
  statusCredits: number;
  nights: number;
}

export type ProgramConfigTier = {
  id: App.LuxLoyaltyTier;
  statusCredits: number;
  nights: number;
};

/**
 * Retain requirements apply when the tier review falls in the current membership year.
 * Bronze is excluded — there is no tier below it to retain from.
 */
export function canShowTierProgressionRetain(
  tier: App.LuxLoyaltyTier,
  membershipYearContext: MembershipYearContext | undefined,
): boolean {
  return tier !== 'bronze' && membershipYearContext?.retainVariant === 'current-year';
}

/**
 * Resolves display thresholds from the account API, falling back to program-config
 * tier thresholds when the API nullifies zeros (same as customer portal).
 */
export function resolveTierProgressionThresholds(
  account: App.LuxLoyaltyAccount,
  mode: 'retain' | 'upgrade',
  programConfigTiers?: ProgramConfigTier[],
): TierProgressionThresholds {
  const tierById = programConfigTiers
    ? Object.fromEntries(programConfigTiers.map((tier) => [tier.id, tier]))
    : undefined;
  const nextTier = getNextLuxLoyaltyTier(account.tier);
  const currentTierConfig = tierById?.[account.tier];
  const nextTierConfig = nextTier ? tierById?.[nextTier] : undefined;

  if (mode === 'retain') {
    return {
      statusCredits: account.totalCreditsForTierRetention || currentTierConfig?.statusCredits || 0,
      nights: account.numberOfNightsForTierRetention || currentTierConfig?.nights || 0,
    };
  }

  return {
    statusCredits: account.totalCreditsForTierUpgrade || nextTierConfig?.statusCredits || 0,
    nights: account.numberOfNightsForTierUpgrade || nextTierConfig?.nights || 0,
  };
}

export function hasTierProgressionThresholdData(thresholds: TierProgressionThresholds): boolean {
  return thresholds.statusCredits > 0 || thresholds.nights > 0;
}

/** Upgrade requirements apply for all tiers below Platinum. */
export function canShowTierProgressionUpgrade(tier: App.LuxLoyaltyTier): boolean {
  return tier !== 'platinum' && tier !== 'infinite';
}

/** Toggle when both retain (in review year) and upgrade (below Platinum) are relevant. */
export function canToggleTierProgressionView(canShowRetain: boolean, canShowUpgrade: boolean): boolean {
  return canShowRetain && canShowUpgrade;
}
